export function formatPayMonthLabel(payMonth) {
  const s = String(payMonth || "").slice(0, 7);
  const m = s.match(/^(\d{4})-(\d{2})$/);
  if (!m) return s;
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const idx = Number(m[2]) - 1;
  const yy = String(m[1]).slice(-2);
  return `${months[idx] || m[2]}-${yy}`;
}

/** Previous calendar pay month: 2026-07 → 2026-06 */
export function getPreviousPayMonth(payMonth) {
  const s = String(payMonth || "").slice(0, 7);
  const m = s.match(/^(\d{4})-(\d{2})$/);
  if (!m) return null;
  let year = Number(m[1]);
  let month = Number(m[2]) - 1;
  if (month < 1) {
    month = 12;
    year -= 1;
  }
  return `${year}-${String(month).padStart(2, "0")}`;
}

/** Next calendar pay month: 2026-07 → 2026-08 */
export function getNextPayMonth(payMonth) {
  const s = String(payMonth || "").slice(0, 7);
  const m = s.match(/^(\d{4})-(\d{2})$/);
  if (!m) return null;
  let year = Number(m[1]);
  let month = Number(m[2]) + 1;
  if (month > 12) {
    month = 1;
    year += 1;
  }
  return `${year}-${String(month).padStart(2, "0")}`;
}

/**
 * Run Payroll pay-month picker max:
 * - no finalised history → company min only
 * - else → month after latest finalised
 * - stickyMax (openedThrough) never shrinks when earlier months are unlocked
 */
export function resolvePayMonthSelectableMax(
  finalizedMonths,
  companyMinMonth,
  stickyMaxMonth = null
) {
  const min = String(companyMinMonth || "").slice(0, 7);
  const list = (Array.isArray(finalizedMonths) ? finalizedMonths : [])
    .map((m) => String(m || "").slice(0, 7))
    .filter((m) => /^\d{4}-\d{2}$/.test(m));

  let fromFinalized = null;
  if (list.length) {
    const latest = list.reduce((a, b) => (a > b ? a : b));
    fromFinalized = getNextPayMonth(latest);
  }

  const sticky = String(stickyMaxMonth || "").slice(0, 7);
  const stickyOk = /^\d{4}-\d{2}$/.test(sticky) ? sticky : null;
  const finalizedOk = /^\d{4}-\d{2}$/.test(fromFinalized || "")
    ? fromFinalized
    : null;

  const candidates = [finalizedOk, stickyOk].filter(Boolean);
  if (!candidates.length) {
    return /^\d{4}-\d{2}$/.test(min) ? min : null;
  }
  const max = candidates.reduce((a, b) => (a > b ? a : b));
  if (/^\d{4}-\d{2}$/.test(min) && max < min) return min;
  return max;
}

/**
 * Default Run Payroll month:
 * start at service commencement; while that month is finalised and before
 * current (and within selectable max), move to the next month.
 * Result is the first open month to work, capped at current calendar month.
 */
export function resolveDefaultPayMonth({
  companyMinMonth,
  companyMaxMonth = null,
  finalizedMonths = [],
  currentMonth = null,
} = {}) {
  const min = String(companyMinMonth || "").slice(0, 7);
  const now = String(currentMonth || new Date().toISOString().slice(0, 7)).slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(min)) {
    return /^\d{4}-\d{2}$/.test(now) ? now : null;
  }
  const finalized = new Set(
    (Array.isArray(finalizedMonths) ? finalizedMonths : [])
      .map((m) => String(m || "").slice(0, 7))
      .filter((m) => /^\d{4}-\d{2}$/.test(m))
  );
  let endCap = /^\d{4}-\d{2}$/.test(now) ? now : min;
  const max = String(companyMaxMonth || "").slice(0, 7);
  if (/^\d{4}-\d{2}$/.test(max) && max < endCap) endCap = max;
  if (endCap < min) endCap = min;

  let cursor = min;
  while (cursor && cursor < endCap && finalized.has(cursor)) {
    const next = getNextPayMonth(cursor);
    if (!next || next === cursor) break;
    cursor = next;
  }
  return cursor;
}

/** True if master LWD/termination is before this pay month (out of scope). */
export function isEmployeeOutOfPayMonth(employee, payMonth) {
  const ym = String(payMonth || "").slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(ym) || !employee) return false;
  // Prefer real LWD fields. Do not treat terminationDate on active employees as exit
  // (that wrongly emptied payroll scope and flagged everyone as "left earlier").
  let raw =
    employee.relievedDate ||
    employee.lastWorkingDay ||
    employee.leaveDate ||
    employee.dateOfLeaving ||
    null;
  if (!raw) {
    const status = String(employee.employmentStatus || "").trim().toLowerCase();
    const left =
      status === "relieved" || status === "resigned" || status === "terminated";
    if (!left) return false;
    raw =
      employee.terminationDate ||
      employee.resignationDate ||
      employee.exitDate ||
      employee.endDate ||
      employee.separationDate ||
      null;
  }
  if (!raw) return false;
  const exit = String(raw).trim();
  const m = exit.match(/^(\d{4})-(\d{2})/);
  if (!m) return false;
  const exitMonth = `${m[1]}-${m[2]}`;
  return exitMonth < ym;
}

export function chunkIds(ids, size = 15) {
  const list = Array.isArray(ids) ? ids : [];
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

export function stepEmptyMessage(stepKey, payMonth) {
  const label = formatPayMonthLabel(payMonth);
  const map = {
    left_employees: `No Left employees for ${label}`,
    salary_revisions: `No Revisions for ${label}`,
    lop_reversal_arrears: `No LOP Reversal & Arrears for ${label}`,
    bank_details: `No Bank changes for ${label}`,
    new_joinees: `No New Joinees for ${label}`,
    one_time_inputs: `No Monthly Variables for ${label}`,
    attendance_validation: `No attendance issues for ${label}`,
  };
  return map[stepKey] || `No records for ${label}`;
}

export function filterEmployeesByApplicability(employees, policy) {
  if (!policy || policy.allEmployees) return employees || [];
  return (employees || []).filter((emp) => {
    if (policy.branch && String(emp.branchId) !== String(policy.branch)) return false;
    if (policy.department && String(emp.departmentId) !== String(policy.department)) return false;
    if (policy.designation && String(emp.designationId) !== String(policy.designation)) return false;
    if (policy.grade && String(emp.gradeId) !== String(policy.grade)) return false;
    if (policy.category && String(emp.categoryId) !== String(policy.category)) return false;
    if (policy.subDepartment && String(emp.subDepartmentId) !== String(policy.subDepartment)) return false;
    if (policy.location && String(emp.location || "") !== String(policy.location)) return false;
    if (Array.isArray(policy.employmentTypes) && policy.employmentTypes.length) {
      const t = String(emp.employmentType || "").trim();
      if (!policy.employmentTypes.includes(t)) return false;
    }
    return true;
  });
}

/** Rounded amount, Indian grouping, no currency symbol (payroll grids). */
export function formatInr(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return Math.round(n).toLocaleString("en-IN", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });
}
